import ace from "ace-builds";
import "ace-builds/src-noconflict/theme-tomorrow_night";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-yaml";
import "ace-builds/src-noconflict/mode-xml";
import "ace-builds/src-noconflict/mode-properties";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/mode-lua";
import { useEffect, useRef } from "react";

export interface ValueEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language: string;
  readOnly?: boolean;
  height?: number;
}

export function ValueEditor({ value, onChange, language, readOnly, height }: ValueEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<ace.Ace.Editor | null>(null);
  const onChangeRef = useRef(onChange);
  // Ace emits "change" for programmatic setValue too; without this guard a
  // value loaded from the server would mark the editor as user-dirty.
  const syncingRef = useRef(false);
  onChangeRef.current = onChange;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const editor = ace.edit(container, {
      fontSize: 13,
      showPrintMargin: false,
      useWorker: false,
      tabSize: 2,
      wrap: true,
      highlightActiveLine: false,
    });
    editor.setTheme("ace/theme/tomorrow_night");
    editor.setOption("fontFamily", "'IBM Plex Mono', monospace");
    editor.on("change", () => {
      if (!syncingRef.current) {
        onChangeRef.current?.(editor.getValue());
      }
    });
    editorRef.current = editor;
    // Ace measures once at mount; re-measure when the container changes size
    // (drawer/modal open animations, window resizes, panel drags).
    const observer = new ResizeObserver(() => editor.resize());
    observer.observe(container);
    return () => {
      observer.disconnect();
      editor.destroy();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.getValue() !== value) {
      syncingRef.current = true;
      // session.setValue (unlike editor.setValue) also resets the undo stack,
      // so Cmd+Z can never resurrect a previously loaded entry's text.
      editor.session.setValue(value);
      syncingRef.current = false;
    }
  }, [value]);

  useEffect(() => {
    editorRef.current?.session.setMode(`ace/mode/${language}`);
  }, [language]);

  useEffect(() => {
    editorRef.current?.setReadOnly(readOnly ?? false);
  }, [readOnly]);

  return <div ref={containerRef} className="value-editor" style={{ height: height ?? 320 }} />;
}
